import {
  HeroContent,
  ProjectOverviewContent,
  ProjectHighlightsContent,
  FaqContent,
  LocationContent,
  PaymentPlanContent,
  GalleryContent,
  LegalInfoContent,
  LeadFormContent,
  DownloadFormContent
} from '../types/pageBuilder';

// Default coordinates for Dubai
export const DEFAULT_COORDINATES = {
  lat: 25.069750,
  lng: 55.247471
};

// Default Hero Content
export const DEFAULT_HERO_CONTENT: HeroContent = {
  title: '',
  subtitle: '',
  highlights: ['', '', '', ''],
  backgroundImage: '',
  backgroundImageId: undefined,
  primaryButtonText: 'Download Brochure',
  secondaryButtonText: 'Speak to Property Expert',
  showDownloadForm: true
};

// Default Project Overview Content
export const DEFAULT_PROJECT_OVERVIEW_CONTENT: ProjectOverviewContent = {
  title: '',
  status: 'Off-Plan',
  description: '',
  sellingPoints: [],
  unitTypes: []
};

// Default Project Highlights Content
export const DEFAULT_PROJECT_HIGHLIGHTS_CONTENT: ProjectHighlightsContent = {
  features: [],
  developer: {
    name: '',
    description: '',
    logo: '',
    videoUrl: ''
  }
};

// Default FAQ Content
export const DEFAULT_FAQ_CONTENT: FaqContent = {
  faqs: []
};

// Default Location Content
export const DEFAULT_LOCATION_CONTENT: LocationContent = {
  projectName: '',
  coordinates: DEFAULT_COORDINATES,
  nearbyPlaces: [],
  keyDistances: []
};

// Default Payment Plan Content
export const DEFAULT_PAYMENT_PLAN_CONTENT: PaymentPlanContent = {
  title: '',
  description: '',
  plans: []
};

// Default Gallery Content
export const DEFAULT_GALLERY_CONTENT: GalleryContent = {
  images: []
};

// Default Legal Info Content
export const DEFAULT_LEGAL_INFO_CONTENT: LegalInfoContent = {
  dldPermitNumber: '',
  qrCodeUrl: ''
};

// Form Options
export const BUDGET_OPTIONS = [
  { value: '500k-1m', label: 'AED 500K - 1M' },
  { value: '1m-2m', label: 'AED 1M - 2M' },
  { value: '2m-5m', label: 'AED 2M - 5M' },
  { value: '5m+', label: 'AED 5M+' }
];

export const TIMELINE_OPTIONS = [
  { value: 'immediate', label: 'Immediate' },
  { value: '3months', label: 'Within 3 months' },
  { value: '6months', label: 'Within 6 months' },
  { value: '1year', label: 'Within 1 year' }
];

export const BEDROOM_OPTIONS = [
  { value: 'studio', label: 'Studio' },
  { value: '1bed', label: '1 Bedroom' },
  { value: '2bed', label: '2 Bedrooms' },
  { value: '3bed', label: '3 Bedrooms' },
  { value: '4bed+', label: '4+ Bedrooms' }
];

export const INTEREST_OPTIONS = [
  { value: 'investment', label: 'Investment opportunities' },
  { value: 'end-user', label: 'Personal residence' },
  { value: 'agent', label: 'Real estate agent' },
  { value: 'exploring', label: 'Just exploring' }
];

export const PREVIOUS_PURCHASES_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: '1-2', label: '1-2 properties' },
  { value: '3-5', label: '3-5 properties' },
  { value: '5+', label: '5+ properties' }
];

export const INVESTMENT_TIMELINE_OPTIONS = [
  { value: 'immediate', label: 'Immediately' },
  { value: '3months', label: 'Within 3 months' },
  { value: '6months', label: 'Within 6 months' },
  { value: 'exploring', label: 'Just exploring' }
];

// Default Lead Form Content
export const DEFAULT_LEAD_FORM_CONTENT: LeadFormContent = {
  title: 'Get in Touch',
  description: 'Contact us to learn more about this project and schedule a viewing.',
  stepTitles: {
    basicInfo: 'Let\'s get to know you',
    purpose: 'What brings you here today?',
    investment: 'Investment Preferences',
    endUser: 'Your Dream Home',
    contact: 'How would you like us to contact you?'
  },
  stepDescriptions: {
    basicInfo: 'Enter your details to receive personalized information',
    purpose: 'Select your primary interest',
    investment: 'Tell us about your investment preferences',
    endUser: 'Tell us about your home preferences',
    contact: 'Choose your preferred contact method'
  },
  purposeDescriptions: {
    investment: 'Looking for investment opportunities',
    endUser: 'Looking for a home to live in'
  },
  contactDescriptions: {
    whatsapp: 'Quick and convenient messaging',
    phone: 'Direct phone conversation',
    email: 'Detailed information via email'
  },
  labels: {
    fullName: 'Full Name',
    email: 'Email Address',
    phone: 'Phone Number',
    whatsYourPurpose: 'What brings you here today?',
    investment: 'Investment',
    endUser: 'End User',
    investmentPreferences: 'Investment Preferences',
    investmentBudget: 'Investment Budget',
    investmentTimeline: 'Investment Timeline',
    yourDreamHome: 'Your Dream Home',
    preferredSize: 'Preferred Size',
    budgetRange: 'Budget Range',
    howToContact: 'How would you like us to contact you?',
    whatsapp: 'WhatsApp',
    phoneCall: 'Phone Call',
    emailOption: 'Email',
    continue: 'Continue',
    back: 'Back',
    submit: 'Submit',
    submitting: 'Submitting...',
    thankYou: 'Thank You!',
    willContactYou: 'Thank you for your interest! We will contact you soon.'
  },
  budgetOptions: BUDGET_OPTIONS,
  timelineOptions: TIMELINE_OPTIONS,
  bedroomOptions: BEDROOM_OPTIONS,
  validationMessages: {
    nameRequired: 'Name is required',
    emailRequired: 'Email is required',
    emailInvalid: 'Please enter a valid email address',
    phoneRequired: 'Phone number is required',
    phoneInvalid: 'Please enter a valid phone number'
  },
  fields: {
    name: true,
    email: true,
    phone: true,
    purpose: true,
    budget: true,
    timeline: true,
    bedrooms: true,
    contactPreference: true
  },
  submitButtonText: 'Submit',
  successMessage: 'Thank you for your interest! We will contact you soon.',
  privacyText: 'By submitting this form, you agree to our privacy policy and terms of service.',
  success: {
    title: 'Thank You!',
    description: 'Thank you for your interest! We will contact you soon.'
  },
  error: {
    title: 'Submission Error',
    description: 'Failed to submit the form. Please try again later.'
  }
};

// Default Download Form Content
export const DEFAULT_DOWNLOAD_FORM_CONTENT: DownloadFormContent = {
  title: 'Get Personalized Property Details',
  subtitle: 'Speak with our property experts and get:',
  welcomeTitle: 'Get Personalized Property Details',
  welcomeDescription: 'Speak with our property experts and get:',
  benefits: [
    'Detailed floor plans & pricing',
    'Virtual tour of the property',
    'Payment plan options',
    'Investment analysis report'
  ],
  stepTitles: {
    basicInfo: 'Basic Information',
    contact: 'Contact Details',
    interest: 'Your Interest',
    experience: 'Experience'
  },
  stepDescriptions: {
    basicInfo: 'Tell us about yourself',
    contact: 'How can we reach you?',
    interest: 'What interests you most?',
    experience: 'Your property experience'
  },
  labels: {
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email Address',
    phone: 'Phone Number',
    interest: 'What interests you most?',
    previousPurchases: 'Previous property purchases',
    investmentTimeline: 'Investment timeline',
    continue: 'Continue',
    back: 'Back',
    submit: 'Download Brochure',
    submitting: 'Submitting...',
    thankYou: 'Thank You!',
    willContactYou: 'Your brochure request has been received. We\'ll send it to your email shortly.'
  },
  validationMessages: {
    firstNameRequired: 'First name is required',
    lastNameRequired: 'Last name is required',
    emailRequired: 'Email is required',
    emailInvalid: 'Please enter a valid email address',
    phoneRequired: 'Phone number is required'
  },
  interestOptions: INTEREST_OPTIONS,
  previousPurchasesOptions: PREVIOUS_PURCHASES_OPTIONS,
  investmentTimelineOptions: INVESTMENT_TIMELINE_OPTIONS,
  success: {
    title: 'Thank You!',
    description: 'Your brochure request has been received. We\'ll send it to your email shortly.'
  },
  error: {
    title: 'Submission Error',
    description: 'Failed to submit the request. Please try again later.'
  },
  privacyText: 'By submitting this form, you agree to our privacy policy and terms of service.'
};

// Initial state for Lead Form (used in component initialization)
export const INITIAL_LEAD_FORM_CONTENT: LeadFormContent = {
  title: '',
  description: '',
  stepTitles: {
    basicInfo: '',
    purpose: '',
    investment: '',
    endUser: '',
    contact: ''
  },
  stepDescriptions: {
    basicInfo: '',
    purpose: '',
    investment: '',
    endUser: '',
    contact: ''
  },
  purposeDescriptions: {
    investment: '',
    endUser: ''
  },
  contactDescriptions: {
    whatsapp: '',
    phone: '',
    email: ''
  },
  labels: {
    fullName: '',
    email: '',
    phone: '',
    whatsYourPurpose: '',
    investment: '',
    endUser: '',
    investmentPreferences: '',
    investmentBudget: '',
    investmentTimeline: '',
    yourDreamHome: '',
    preferredSize: '',
    budgetRange: '',
    howToContact: '',
    whatsapp: '',
    phoneCall: '',
    emailOption: '',
    continue: '',
    back: '',
    submit: '',
    submitting: '',
    thankYou: '',
    willContactYou: ''
  },
  budgetOptions: BUDGET_OPTIONS,
  timelineOptions: TIMELINE_OPTIONS,
  bedroomOptions: BEDROOM_OPTIONS,
  validationMessages: {
    nameRequired: '',
    emailRequired: '',
    emailInvalid: '',
    phoneRequired: '',
    phoneInvalid: ''
  },
  fields: {
    name: true,
    email: true,
    phone: true,
    purpose: true,
    budget: true,
    timeline: true,
    bedrooms: true,
    contactPreference: true
  },
  submitButtonText: 'Submit',
  successMessage: 'Thank you for your interest! We will contact you soon.',
  privacyText: 'By submitting this form, you agree to our privacy policy and terms of service.',
  success: {
    title: 'Download Started!',
    description: 'Thank you for your interest. We\'ll review your submission and get back to you soon.'
  },
  error: {
    title: 'Submission Error',
    description: 'Failed to submit the form. Please try again later.'
  }
};

// Initial state for Download Form (used in component initialization)
export const INITIAL_DOWNLOAD_FORM_CONTENT: DownloadFormContent = {
  title: 'Get Personalized Property Details',
  subtitle: 'Speak with our property experts and get:',
  welcomeTitle: 'Get Personalized Property Details',
  welcomeDescription: 'Speak with our property experts and get:',
  benefits: [
    'Detailed floor plans & pricing',
    'Virtual tour of the property',
    'Payment plan options',
    'Investment analysis report'
  ],
  stepTitles: {
    basicInfo: '',
    contact: '',
    interest: '',
    experience: ''
  },
  stepDescriptions: {
    basicInfo: '',
    contact: '',
    interest: '',
    experience: ''
  },
  labels: {
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email Address',
    phone: 'Phone Number',
    interest: 'What interests you most?',
    previousPurchases: 'Previous property purchases',
    investmentTimeline: 'Investment timeline',
    continue: 'Continue',
    back: 'Back',
    submit: 'Download Brochure',
    submitting: 'Submitting...',
    thankYou: 'Thank You!',
    willContactYou: 'Your brochure request has been received. We\'ll send it to your email shortly.'
  },
  validationMessages: {
    firstNameRequired: '',
    lastNameRequired: '',
    emailRequired: '',
    emailInvalid: '',
    phoneRequired: ''
  },
  interestOptions: INTEREST_OPTIONS,
  previousPurchasesOptions: PREVIOUS_PURCHASES_OPTIONS,
  investmentTimelineOptions: INVESTMENT_TIMELINE_OPTIONS,
  success: {
    title: 'Thank You!',
    description: 'Your brochure request has been received. We\'ll send it to your email shortly.'
  },
  error: {
    title: 'Submission Error',
    description: 'Failed to submit the request. Please try again later.'
  },
  privacyText: 'By submitting this form, you agree to our privacy policy and terms of service.'
}; 